/**
 * UpgradeHint.jsx – Gesperrtes Modul-Anzeige Komponente (Phase 2B.3)
 *
 * Zeigt gesperrte Module mit Upgrade-Hinweis.
 * Für Billing-V2-Kunden: sichtbar aber nicht nutzbar.
 * Legacy-Kunden sehen diese Komponente NICHT.
 *
 * Usage:
 *   <UpgradeHint
 *     moduleName="Betriebsvereinbarungen"
 *     requiredPackage="Business"
 *     currentPackage="Starter"
 *   />
 */

import React, { useState } from 'react';
import { Lock, ArrowUp, X, Package } from 'lucide-react';

const PACKAGE_PRICES = {
  starter:         { label: 'Starter',         price: '29,90 €' },
  business:        { label: 'Business',         price: '39,90 €' },
  professional_ai: { label: 'Professional AI',  price: '59,90 €' },
};

export const UpgradeHint = ({
  moduleName = 'Dieses Modul',
  requiredPackage = 'business',
  currentPackage = 'starter',
  children,
}) => {
  const [open, setOpen] = useState(false);
  const required = PACKAGE_PRICES[requiredPackage] || { label: requiredPackage, price: '' };
  const current  = PACKAGE_PRICES[currentPackage]  || { label: currentPackage,  price: '' };

  return (
    <div className="relative" data-testid="upgrade-hint-wrapper">
      {/* Kinder-Inhalt mit Blur + Sperre-Overlay */}
      <div className="relative">
        <div className="pointer-events-none select-none opacity-40 blur-[1px]" aria-hidden="true">
          {children}
        </div>
        <div
          className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 dark:bg-gray-900/60 backdrop-blur-sm rounded-lg cursor-pointer"
          onClick={() => setOpen(true)}
          data-testid="upgrade-hint-overlay"
        >
          <div className="flex flex-col items-center gap-2 p-4 text-center">
            <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full">
              <Lock className="h-6 w-6 text-gray-500" />
            </div>
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {moduleName}
            </p>
            <p className="text-xs text-gray-500">
              Verfügbar ab {required.label}
            </p>
            <button
              className="mt-1 flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-full transition-colors"
              data-testid="upgrade-hint-btn"
            >
              <ArrowUp className="h-3 w-3" />
              Upgrade
            </button>
          </div>
        </div>
      </div>

      {/* Upgrade-Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-6"
            onClick={e => e.stopPropagation()}
            data-testid="upgrade-hint-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                  <Package className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg">
                    Upgrade erforderlich
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Paket-Upgrade notwendig
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <strong>{moduleName}</strong> ist in Ihrem aktuellen Paket nicht verfügbar.
              </p>

              {/* Aktuelles Paket */}
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Ihr Paket</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{current.label}</p>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">{current.price} / Monat</span>
              </div>

              {/* Pfeil */}
              <div className="flex justify-center">
                <ArrowUp className="h-5 w-5 text-blue-500" />
              </div>

              {/* Benötigtes Paket */}
              <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border-2 border-blue-400 dark:border-blue-700">
                <div>
                  <p className="text-xs text-blue-500">Benötigtes Paket</p>
                  <p className="font-bold text-blue-700 dark:text-blue-300">{required.label}</p>
                </div>
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">{required.price} / Monat</span>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Alle Preise Netto zzgl. 19% MwSt. · Monatlich kündbar
              </p>

              <button
                onClick={() => setOpen(false)}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
              >
                Verstanden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * LockedModuleCard – Karte für gesperrte Module in Modul-Listen
 * Zeigt das Modul als "verfügbar ab X" an.
 */
export const LockedModuleCard = ({
  moduleName,
  description,
  requiredPackage = 'business',
  icon: Icon = Lock,
}) => {
  const [showHint, setShowHint] = useState(false);
  const required = PACKAGE_PRICES[requiredPackage] || { label: requiredPackage, price: '' };

  return (
    <>
      <div
        className="p-4 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 cursor-pointer hover:border-blue-300 dark:hover:border-blue-700 transition-colors opacity-75"
        onClick={() => setShowHint(true)}
        data-testid={`locked-module-${moduleName?.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0">
            {Icon ? <Icon className="h-5 w-5 text-gray-400" /> : <Lock className="h-5 w-5 text-gray-400" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-medium text-gray-600 dark:text-gray-400 text-sm">{moduleName}</p>
              <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs rounded-full flex items-center gap-1">
                <Lock className="h-2.5 w-2.5" />
                ab {required.label}
              </span>
            </div>
            {description && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{description}</p>
            )}
          </div>
        </div>
      </div>

      {showHint && (
        <UpgradeHint
          moduleName={moduleName}
          requiredPackage={requiredPackage}
          currentPackage="starter"
        >
          <div className="h-20 bg-gray-100 rounded" />
        </UpgradeHint>
      )}
    </>
  );
};

export default UpgradeHint;
